import { Inject, Injectable } from "@angular/core";
import { Category } from "../Models/category";
import { Response } from "../Models/response";
import { AbstractService } from "./abstract-service.service";
import { BehaviorSubject, Observable, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { APP_CONFIG, AppConfig } from "./config";
import { catchError, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { LanguageService } from "./language.service";

@Injectable({
  providedIn: "root",
})
export class CategoryService extends AbstractService<Category, number> {
  url: string;

  readonly categoriesSubject = new BehaviorSubject<Category[]>([]);

  public readonly categories = this.categoriesSubject.asObservable();

  /** Activated categories of one language, fetched once and shared by the header and every page asking for them. */
  private activated?: { lang: string; categories$: Observable<Category[]> };

  constructor(
    protected http: HttpClient,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private languageService: LanguageService
  ) {
    super(http);
    this.url = `${this.appConfig.apiEndpoint}/new/categories`;

    this.getAllActivated().subscribe({ error: () => {} });
  }

  public getAllActivated(): Observable<Category[]> {
    const lang = this.languageService.current;
    if (this.activated?.lang !== lang) {
      // new language: subscribers of `categories` drop the old-language list until the new one is published
      this.categoriesSubject.next([]);
      const url = `${this.url}/activated`;
      this.activated = {
        lang,
        // a failed call isn't kept (shareReplay resets on error): the next caller tries again
        categories$: this.http.get<Category[]>(url).pipe(
          map((response: Category[]) => {
            if (response) {
              return response;
            } else {
              throw new Error();
            }
          }),
          switchMap((categories) => this.withFrenchLabels(url, categories)),
          tap((categories) => this.categoriesSubject.next(categories)),
          shareReplay(1)
        ),
      };
    }
    return this.activated.categories$;
  }

  /**
   * In Arabic, a category without an Arabic name comes back with a null label; the header, footer and home
   * page all build links from `label` and break on it. Fill those labels with the French name (one extra call,
   * only when a label is missing).
   */
  private withFrenchLabels(
    url: string,
    categories: Category[]
  ): Observable<Category[]> {
    if (categories.every((category) => category.label)) {
      return of(categories);
    }
    return this.http.get<Category[]>(url, { params: { lang: "fr" } }).pipe(
      catchError(() => of([] as Category[])),
      map((french) => {
        const frenchLabels = new Map(
          french.map((category) => [category.id, category.label])
        );
        return categories.map((category) =>
          category.label
            ? category
            : { ...category, label: frenchLabels.get(category.id) || "" }
        );
      })
    );
  }

  public getPopular(): Observable<Response<Category[]>> {
    const url = `${this.url}/popular`;
    return this.http.get<Response<Category[]>>(url);
  }

  public getByConfigurationId(
    configurationId: number
  ): Observable<Response<Category>> {
    const url = `${this.url}/configuration-category/${configurationId}`;
    return this.http.get<Response<Category>>(url);
  }
}
